/**
 * dialRules — source de vérité unique pour les règles de dial.
 *
 * Modèle métier (refactor 2026-05-12, voir ADR-031) :
 *
 *   canCall(prospect, lastCall, opts) → boolean
 *     Droit légal/commercial d'appeler. Si false → le prospect est CACHÉ
 *     du Power Mode ET de la vue par défaut du tableau. Règle stricte,
 *     non-négociable.
 *
 *   dialIntent(prospect, lastCall) → 'fresh' | 'no_answer' | 'contacted'
 *                                   | 'callback' | 'archived'
 *     Catégorie commerciale du prospect. Sert au tri du Power Mode et
 *     au filtrage des 3 vues du tableau (défaut / rappels / archivés).
 *
 *   sortByDialIntent(prospects, lastCallByPhone) → prospects[]
 *     Tri du Power Mode : fresh > no_answer > contacted. Les callbacks
 *     et archivés ne sont JAMAIS dans la pile.
 *
 * Cycle de vie d'un prospect :
 *   1. fresh            → jamais appelé, tier 0 du Power Mode
 *   2. no_answer        → tenté sans humain au bout, tier 1
 *   3. contacted        → humain joint sans conclusion, tier 2
 *   4. callback         → rappel demandé (sort du Power Mode, vue dédiée)
 *   5. archived         → RDV pris / pas intéressé / DNC (caché, vue dédiée)
 *
 * Miroir côté serveur : server/src/domain/calling/entities/ParallelCall.ts
 */

import { PROSPECT_STATUS } from './prospectStatus';
import { isSnoozed } from './snooze';
import { hasMeetingBooked } from '@/utils/callUtils';

// Outcomes "archivés" : on ne rappelle PAS, sauf décision explicite de l'agent
// via "Réactiver" depuis la vue Archivés (qui repasse le prospect en tier 2).
export const ARCHIVED_OUTCOMES = [
  'not_interested',
  'meeting_booked',
];

// Outcomes "callback" : sortent du Power Mode, traités via vue dédiée.
export const CALLBACK_OUTCOMES = [
  'callback_requested',
];

// Outcomes "contacted" (humain joint, mais sans conclusion forte). Restent
// dans la pile générale en tier 2.
export const CONTACTED_OUTCOMES = [
  'contacted',
  'connected_to_agent',
  'interested',
];

// Outcomes "no answer" : tenté, pas de contact humain. Tier 1 du Power Mode.
export const NO_ANSWER_OUTCOMES = [
  'no_answer',
  'not_reachable',
  'voicemail',
  'left_voicemail',
  'failed',
  'canceled',
  'busy',
];

/**
 * A line the dialer hung up itself because another prospect answered first
 * (Kevin, 2026-10-26). The prospect never had the chance to pick up: the call
 * does not count, the prospect stays as it was ("ready to call"), and the
 * status pill only adds an icon.
 */
export const INTERRUPTED_OUTCOME = 'interrupted';
export function isInterruptedCall(call: any): boolean {
  return call?.call_outcome === INTERRUPTED_OUTCOME;
}

/**
 * A status set by hand (Requeue, the card's status select) wins over the last
 * call's outcome when it is newer than that call. 'new' means ready to call:
 * the call history stays, but the prospect is treated as never called.
 * Returns the call to reason about: null for 'new', a synthetic call carrying
 * the chosen outcome, or the real last call when there is no newer override.
 */
export function effectiveLastCall(prospect: any, lastCall: any): any {
  const override = prospect?.status_override;
  const at = prospect?.status_override_at;
  if (!override || !at) return lastCall;
  const callAt = lastCall?.created_date ? new Date(lastCall.created_date).getTime() : 0;
  if (lastCall && callAt > new Date(at).getTime()) return lastCall;
  if (override === 'new') return null;
  return { call_outcome: override, call_status: 'completed', created_date: at, synthetic: true };
}

/**
 * Validation E.164 basique. 8-15 chiffres après nettoyage.
 */
export function isValidPhoneNumber(raw: unknown): boolean {
  if (!raw || typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (/^(https?:|mailto:|www\.)/i.test(trimmed)) return false;
  if (trimmed.includes('@')) return false;
  const digits = trimmed.replace(/[^\d+]/g, '');
  const digitsOnly = digits.replace(/\+/g, '');
  if (digitsOnly.length < 8 || digitsOnly.length > 15) return false;
  return true;
}

/**
 * canCall — droit d'appeler ce prospect MAINTENANT.
 *
 * Blocages (tous nécessaires) :
 *  - téléphone valide
 *  - status != do_not_call / placeholder, flag do_not_call != true
 *  - pas en pause (snoozed_until dans le futur) — #56
 *  - pas déjà dialé dans la session courante
 *  - pas au-dessus de la limite de tentatives
 *  - dernier outcome n'est pas dans ARCHIVED_OUTCOMES
 *  - dernier outcome n'est pas dans CALLBACK_OUTCOMES (les rappels ont
 *    leur propre vue, ils ne passent jamais en Power Mode automatique)
 *  - matche la source sélectionnée
 *
 * @param {object} prospect
 * @param {object|undefined} lastCall — dernier ParallelCall pour ce numéro
 * @param {object} opts
 * @param {Set<string>} opts.dialedThisSession
 * @param {(phone: string) => boolean} opts.isAtCallLimit
 * @param {string} opts.selectedSource
 * @returns {boolean}
 */
export function canCall(prospect: any, lastCall: any, opts: any): boolean {
  const { dialedThisSession, isAtCallLimit, selectedSource, scopeIds } = opts;
  // Power Mode dials the list the agent is looking at, nothing else: when
  // the contacts screen publishes the ids it shows, a prospect outside them
  // is not callable (a "No answer" prospect is not redialed from "Ready to
  // call"; the agent opens the "No answer" list to redial on purpose).
  if (scopeIds && !scopeIds.has(prospect.id)) return false;

  if (!prospect.prospect_phone?.trim()) return false;
  if (!isValidPhoneNumber(prospect.prospect_phone)) return false;
  if (prospect.status === PROSPECT_STATUS.DO_NOT_CALL || prospect.status === PROSPECT_STATUS.PLACEHOLDER) return false;
  if (prospect.do_not_call === true) return false;
  // #56 -- SNOOZE : mise en pause temporaire. ⚠️ Ceci MODIFIE LE DROIT
  // D'APPELER : un prospect en pause sort de la file jusqu'a sa date de fin,
  // meme s'il est dans une liste active. Il n'est ni archive ni DNC -- il
  // redevient appelable tout seul a l'echeance, sans aucune action.
  if (isSnoozed(prospect)) return false;
  // 2026-05-13 — numero non attribue (Twilio rejette instantanement) : skip.
  if (prospect.phone_unassigned === true) return false;
  if (dialedThisSession?.has(prospect.prospect_phone)) return false;
  if (isAtCallLimit && isAtCallLimit(prospect.prospect_phone)) return false;

  // Outcomes qui sortent le prospect du Power Mode.
  // 2026-05-13 — CONTACTED ajoute : un prospect deja joint (humain au bout)
  // ne doit PAS etre rappele automatiquement par le Power Mode. Il reste
  // accessible via le filtre STATUT APPEL "Contactes" pour un rappel manuel
  // explicite, mais sort de la pile "Prets a appeler".
  const effectiveCall = effectiveLastCall(prospect, lastCall);
  const outcome = effectiveCall?.call_outcome;
  // RUI-P4 : un RDV pris porte le drapeau meeting_booked et l'issue
  // 'contacted' (migration 014) ; les deux formes sont archivees.
  if (hasMeetingBooked(effectiveCall)) return false;
  if (outcome && ARCHIVED_OUTCOMES.includes(outcome)) return false;
  if (outcome && CALLBACK_OUTCOMES.includes(outcome)) return false;
  if (outcome && CONTACTED_OUTCOMES.includes(outcome)) return false;

  // Source filter.
  if (selectedSource === 'Revcall Client Dashboard') {
    return !prospect.source || prospect.source === 'Revcall Client Dashboard';
  }
  return prospect.source === selectedSource;
}

/**
 * dialIntent — catégorie commerciale du prospect.
 *
 * Sert au tri du Power Mode (via sortByDialIntent) et au filtrage des 3
 * vues du tableau de contacts (défaut, rappels, archivés). Pure fonction.
 *
 * @param {object} prospect
 * @param {object|undefined} lastCall
 * @returns {'fresh' | 'no_answer' | 'contacted' | 'callback' | 'archived'}
 */
export const LIVE_CALL_STATUSES = ['dialing', 'ringing', 'ai_analyzing', 'human_detected', 'connected_to_agent'];
export const HUMAN_AMD_RESULTS = ['human', 'human_jev', 'human_swoft', 'call_screening'];

/**
 * The outcome a call counts for, also when the caller ended it without
 * qualifying it (2026-10-07: call_outcome stays null, the call is
 * 'completed', and its prospect matched no list at all: neither "Connectés",
 * nor "Pas de réponse", nor "Prêts à appeler"). A finished call with no
 * outcome counts as 'contacted' when a person was reached (bridged to the
 * caller, or a person verdict), 'no_answer' otherwise. A live call keeps its
 * status.
 */
export function callOutcomeOf(call: any): string {
  if (!call) return '';
  if (call.call_outcome) return call.call_outcome;
  const status = call.call_status || '';
  if (LIVE_CALL_STATUSES.includes(status)) return status;
  if (call.connected_at || call.agent_bridged_at || HUMAN_AMD_RESULTS.includes(call.amd_result)) return 'contacted';
  if (status === 'voicemail') return 'voicemail';
  return status ? 'no_answer' : '';
}

export function dialIntent(prospect: any, lastCall: any): 'fresh' | 'no_answer' | 'contacted' | 'callback' | 'archived' {
  // DNC est un sous-cas d'archivé pour l'affichage (vue archivés).
  if (prospect.status === PROSPECT_STATUS.DO_NOT_CALL || prospect.do_not_call === true) {
    return 'archived';
  }
  // 2026-05-13 — numero non attribue : meme bucket que les archives pour
  // que la vue par defaut le cache et que l'agent y accede via le filtre
  // STATUT APPEL "Numero invalide".
  if (prospect.phone_unassigned === true) {
    return 'archived';
  }

  const effective = effectiveLastCall(prospect, lastCall);
  if (prospect?.status_override === 'new' && effective === null) return 'fresh';
  lastCall = effective;
  // The page loads the 500 most recent calls only: an older last call is
  // known from the prospect's own last_call_status (stamped by the server).
  const outcome = lastCall ? callOutcomeOf(lastCall) : (prospect?.last_call_status || '');

  if (hasMeetingBooked(lastCall)) return 'archived';
  if (outcome && ARCHIVED_OUTCOMES.includes(outcome)) return 'archived';
  if (outcome && CALLBACK_OUTCOMES.includes(outcome)) return 'callback';
  if (outcome && CONTACTED_OUTCOMES.includes(outcome)) return 'contacted';
  if (outcome && NO_ANSWER_OUTCOMES.includes(outcome)) return 'no_answer';

  // Pas de dernier appel OU outcome inconnu → considéré comme jamais appelé.
  if (!lastCall && !prospect.last_call_at) return 'fresh';

  // Cas limite : last_call_at sans outcome exploitable → no_answer (sûr).
  return 'no_answer';
}

const DIAL_INTENT_TIER = {
  fresh: 0,
  no_answer: 1,
  contacted: 2,
  // callback et archived ne sont jamais dans la pile — tier élevé
  // pour qu'un appel d'un consommateur qui les filtre mal les renvoie
  // visiblement en bas.
  callback: 98,
  archived: 99,
};

/**
 * Tri du Power Mode : fresh > no_answer > contacted, stable.
 * Les prospects 'callback' et 'archived' ne devraient PAS être dans
 * cette liste (canCall les filtre en amont), mais s'ils y sont par
 * erreur, ils tombent tout en bas.
 *
 * À intent égal `no_answer`, on trie par nombre de tentatives CROISSANT :
 * un prospect tenté 1 fois passe avant un tenté 2 fois, etc. Logique
 * d'équité — on redonne une chance à tout le monde avant d'insister sur
 * les mêmes. Nécessite `attemptsByPhone` (compteur de calls par phone) ;
 * sans lui, le comportement reste le tri par tiers seul (rétrocompat).
 *
 * @param {object[]} prospects
 * @param {Record<string, object>} lastCallByPhone
 * @param {Record<string, number>} [attemptsByPhone] - nb de calls historiques par phone
 * @returns {object[]}
 */
export function sortByDialIntent(prospects: any[], lastCallByPhone: Record<string, any>, attemptsByPhone?: Record<string, number>): any[] {
  return [...prospects].sort((a, b) => {
    const intentA = dialIntent(a, lastCallByPhone[a.prospect_phone]);
    const intentB = dialIntent(b, lastCallByPhone[b.prospect_phone]);
    const tiers = DIAL_INTENT_TIER as Record<string, number>;
    const tierDiff = (tiers[intentA] ?? 0) - (tiers[intentB] ?? 0);
    if (tierDiff !== 0) return tierDiff;
    // Tie-breaker : à intent égal no_answer, moins de tentatives d'abord.
    if (attemptsByPhone && intentA === 'no_answer' && intentB === 'no_answer') {
      const attA = attemptsByPhone[a.prospect_phone] || 0;
      const attB = attemptsByPhone[b.prospect_phone] || 0;
      if (attA !== attB) return attA - attB;
    }
    // Then the priority set on the card (High 7, Medium 3, Low 0): it was
    // saved but never used, so the High/Medium/Low buttons changed nothing.
    return (b.priority || 0) - (a.priority || 0);
  });
}

/**
 * @deprecated ADR-032 — prospect.status n'est plus muté après un appel.
 * Le call_outcome du ParallelCall suffit pour piloter dialIntent() et canCall().
 * Conservé pour rétrocompat — retourne toujours 'active' (qui passe par
 * tous les filtres normalizés).
 */
export function resolveProspectStatusAfterCall(_callOutcome: string | null | undefined): string {
  return PROSPECT_STATUS.ACTIVE;
}

/**
 * Réactivation d'un prospect archivé (RDV pris, pas intéressé) ou d'un
 * callback traité.
 *
 * ADR-031 Option B + ADR-032 : prospect.status passe toujours à 'active'.
 * Le tier 2 (contacted) après réactivation d'un humain joint est porté
 * en REMPLAÇANT l'outcome archivé par 'contacted' — sinon canCall continue
 * à bloquer. Pour un DNC sans historique d'appel, on efface simplement.
 *
 * @param {object|undefined} lastCall
 * @returns {{ prospectStatus: string, newOutcome: string|null }}
 */
export function reactivationTarget(lastCall: any): { prospectStatus: string; newOutcome: string | null } {
  const outcome = lastCall?.call_outcome;
  const hadHumanContact = outcome && (
    CONTACTED_OUTCOMES.includes(outcome) ||
    ARCHIVED_OUTCOMES.includes(outcome) // "pas intéressé" / "RDV pris" → humain joint
  );
  return {
    prospectStatus: PROSPECT_STATUS.ACTIVE,
    // hadHumanContact → outcome devient 'contacted' (tier 2 dialIntent).
    // Sinon → null (efface, redevient fresh ou no_answer selon last_call_at).
    newOutcome: hadHumanContact ? 'contacted' : null,
  };
}

// ─── Helpers de migration (rétrocompat) ───────────────────────────────────
// L'ancien code utilisait isDialable() / isRedialable() / sortByDialPriority().
// On les exporte comme alias dépréciés pour éviter de casser l'import dans
// les modules pas encore migrés. À supprimer après vérif que rien ne les
// utilise plus (grep zéro résultat).
//
// @deprecated use canCall
export const isDialable = canCall;
// @deprecated use sortByDialIntent
export const sortByDialPriority = sortByDialIntent;
// @deprecated use dialIntent + filtre 'callback' / 'no_answer'
// Conservé pour ne pas casser l'import si un fichier traîne — mais retourne
// désormais TRUE uniquement pour intent === 'no_answer' (ancienne sémantique
// "Relancer les sans-réponse"). Le bouton qui l'utilisait a été supprimé.
export function isRedialable(prospect: any, lastCall: any): boolean {
  if (!lastCall) return false;
  return dialIntent(prospect, lastCall) === 'no_answer';
}
